import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaBan, FaLock, FaEye } from 'react-icons/fa';
import { IoIosRefresh } from 'react-icons/io';
import PageHeader from '../../molecules/PageHeader';
import PageFooter from '../../molecules/PageFooter';
import ATable, { ATableHeader } from '../../organisms/ATable';
import Dialog from '../../organisms/Dialog';
import SelectFilter from '../../organisms/SelectFilter';
import Loading from '../../ions/Loading';
import BadgeStatus from '../../atoms/BadgeStatus';
import { fundingApi, Instrument, InstrumentProduct, Platform, PuDesagiadoCheckResult, RateType, RegistrationType } from '../../../services/fundingApi';
import { extractAndConvertFrom } from '../../../utils/DateUtils';
import { currencyWithPrefix } from '../../../utils/currencyUtils';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'indigo' | 'pink';

// Contrato real: docs/api-contract.md (serviço funding, branch AF-3402).
// Só existem os produtos LCA/CDB/DB_DI - LCI/CRI/CRA não são suportados pelo
// backend. DB_DI não tem PU Desagiado (ver cetipAccountFor/render abaixo).
const PRODUCT_OPTIONS = [
    { value: 'LCA', label: 'LCA' },
    { value: 'CDB', label: 'CDB' },
    { value: 'DB_DI', label: 'DB DI' },
];

const RATE_TYPE_OPTIONS = [
    { value: 'PRE', label: 'PRE' },
    { value: 'POS', label: 'POS' },
];

const STATUS_OPTIONS = [
    { value: 'DRAFT', label: 'Rascunho' },
    { value: 'CUSTODY_REGISTERED', label: 'Custódia registrada' },
    { value: 'COLLATERAL_REGISTERED', label: 'Passivo registrado' },
    { value: 'FULLY_REGISTERED', label: 'Totalmente registrado' },
    { value: 'CANCELLED', label: 'Cancelado' },
];

const STATUS_BADGE_COLOR: Record<string, BadgeColor> = {
    DRAFT: 'gray',
    CUSTODY_REGISTERED: 'blue',
    COLLATERAL_REGISTERED: 'indigo',
    FULLY_REGISTERED: 'green',
    CANCELLED: 'red',
};

const REGISTRATION_BADGE_COLOR: Record<string, BadgeColor> = {
    PENDING: 'yellow',
    SUCCESS: 'green',
    FAILED: 'red',
};

const TABLE_HEADERS: ATableHeader[] = [
    { id: 'col-product', title: 'Produto', field: 'product', canSort: true, width: '80px' },
    { id: 'col-command', title: 'Nº Comando', field: 'commandNumber', canSort: true, width: '100px' },
    { id: 'col-platform', title: 'Corretora', field: 'platformId', canSort: true, width: '110px' },
    { id: 'col-issue-date', title: 'Data Aplicação', field: 'issueDate', canSort: true, width: '120px' },
    { id: 'col-created-at', title: 'Cadastrado em', field: 'createdAt', canSort: true, width: '150px' },
    { id: 'col-due-date', title: 'Vencimento', field: 'dueDate', canSort: true, width: '110px' },
    { id: 'col-index', title: 'Indexador', field: 'rateType', canSort: true, width: '100px' },
    { id: 'col-tax', title: 'Taxa', field: 'tax', canSort: true, width: '90px' },
    { id: 'col-quantity', title: 'Qtde', field: 'quantity', canSort: true, width: '80px' },
    { id: 'col-pu', title: 'PU Desagiado', field: 'puDegassed', canSort: true, width: '140px' },
    { id: 'col-status', title: 'Status', field: 'status', canSort: true, width: '160px' },
    { id: 'col-custody', title: 'Custódia (B3)', field: '', canSort: false, width: '140px' },
    { id: 'col-collateral', title: 'Passivo (Dimensa)', field: '', canSort: false, width: '150px' },
    { id: 'col-actions', title: 'Ações', field: '', canSort: false, width: '150px' },
];

const parseDecimal = (value: string): number => {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const formatTax = (value: string): string => `${parseDecimal(value).toFixed(2)}%`;

// PRE não tem índice (remunerationIndex vem null do backend); POS sempre tem
// (ex: CDI, IPCA) - ver docs/context/business-context.md secao Ubiquitous Language.
const formatIndexador = (instrument: Instrument): string =>
    instrument.rateType === 'POS' ? `${instrument.remunerationIndex || '—'} (POS)` : (instrument.rateType || '—');

// createdAt vem como ISO datetime completo (ex: 2026-08-18T14:32:10+00:00) - mostra data + hora local.
const formatCreatedAt = (value: string): string => {
    const date = new Date(value);
    const datePart = date.toLocaleDateString('pt-BR');
    const timePart = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return `${datePart} ${timePart}`;
};

const findRegistration = (instrument: Instrument, type: RegistrationType) =>
    instrument.registrations.find((r) => r.type === type);

// Máquina de estados documentada em docs/api-contract.md
const canRegisterCustody = (status: string) => status === 'DRAFT' || status === 'COLLATERAL_REGISTERED';
const canRegisterCollateral = (status: string) => status === 'DRAFT' || status === 'CUSTODY_REGISTERED';
const isEditable = (status: string) => status === 'DRAFT';

type EditForm = {
    product: InstrumentProduct;
    issueDate: string;
    dueDate: string;
    rateType: RateType;
    remunerationIndex: string;
    tax: string;
    quantity: string;
    pu: string;
    puDegassed: string;
    financialValue: string;
    diFuturo: string;
    additionalSpread: string;
};

const IssuanceListPage: React.FC = () => {
    const navigate = useNavigate();

    const [instruments, setInstruments] = useState<Instrument[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [total, setTotal] = useState(0);
    const [includeCancelled, setIncludeCancelled] = useState(false);

    const [filterProduct, setFilterProduct] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterPlatform, setFilterPlatform] = useState('');
    const [filterCreatedFrom, setFilterCreatedFrom] = useState('');
    const [filterCreatedTo, setFilterCreatedTo] = useState('');
    const [fieldSorted, setFieldSorted] = useState('dueDate');
    const [orderSorted, setOrderSorted] = useState<'ASC' | 'DESC'>('DESC');

    const showEditModal = useSignal(false);
    const editingInstrument = useSignal<Instrument | null>(null);
    const editForm = useSignal<EditForm | null>(null);
    const isSending = useSignal(false);
    const busyId = useSignal<string | null>(null);

    const showDetailsModal = useSignal(false);
    const viewingInstrument = useSignal<Instrument | null>(null);
    const puCheck = useSignal<PuDesagiadoCheckResult | null>(null);
    const puCheckLoading = useSignal(false);

    // Cache local das ~12 plataformas (GET /platforms) só para exibir o nome
    // da corretora na coluna - carregado uma vez, não faz parte da paginação
    // de instrumentos.
    const platformsById = useSignal<Record<string, Platform>>({});

    useEffect(() => {
        const loadPlatforms = async () => {
            const result = await fundingApi.listPlatforms();
            if (!result.ok) {
                toast.error(`Não foi possível carregar as corretoras: ${result.message}`);
                return;
            }
            platformsById.value = Object.fromEntries(result.data.map((p) => [p.id, p]));
        };
        loadPlatforms();
    }, []);

    const platformName = (platformId: string): string => platformsById.value[platformId]?.name || platformId;

    // Conta CETIP é por produto desde 2026-08-19 (Daycoval usa contas diferentes
    // para LCA e CDB) - resolve a conta certa para o produto do instrumento.
    // DB_DI usa a conta da contraparte (cetipAccountDi), não de corretora.
    const cetipAccountFor = (platformId: string, product: InstrumentProduct): string | null => {
        const platform = platformsById.value[platformId];
        if (!platform) return null;
        if (product === 'DB_DI') return platform.cetipAccountDi;
        return product === 'LCA' ? platform.cetipAccountLca : platform.cetipAccountCdb;
    };

    const platformOptions = Object.values(platformsById.value)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((p) => ({ value: p.id, label: p.name }));

    const loadInstruments = useCallback(async () => {
        setIsLoading(true);
        const result = await fundingApi.listInstruments(page, pageSize, includeCancelled);
        setIsLoading(false);

        if (!result.ok) {
            toast.error(result.message);
            if (result.status === 401 || result.status === 403) navigate('/denied');
            return;
        }

        setInstruments(result.data.items);
        setTotal(result.data.total);
    }, [page, pageSize, includeCancelled, navigate]);

    useEffect(() => {
        loadInstruments();
    }, [loadInstruments]);

    const clearFilters = () => {
        setFilterProduct('');
        setFilterStatus('');
        setFilterPlatform('');
        setFilterCreatedFrom('');
        setFilterCreatedTo('');
    };

    const handleSort = (field: string, order: 'ASC' | 'DESC') => {
        setFieldSorted(field);
        setOrderSorted(order);
    };

    // A API de funding só suporta paginação (page/size) e includeCancelled
    // (ver docs/api-contract.md) - não há filtro de produto/status/ordenação
    // no backend. Produto/Status/ordenação abaixo filtram e ordenam apenas
    // os itens já carregados na página atual.
    const visibleInstruments = instruments
        .filter((row) => !filterProduct || row.product === filterProduct)
        .filter((row) => !filterStatus || row.status === filterStatus)
        .filter((row) => !filterPlatform || row.platformId === filterPlatform)
        // createdAt é datetime completo (YYYY-MM-DDTHH:mm:ss...); comparar só os
        // 10 primeiros caracteres (YYYY-MM-DD) contra o valor do <input type="date">.
        .filter((row) => !filterCreatedFrom || row.createdAt.slice(0, 10) >= filterCreatedFrom)
        .filter((row) => !filterCreatedTo || row.createdAt.slice(0, 10) <= filterCreatedTo)
        .slice()
        .sort((a, b) => {
            const rawA = fieldSorted === 'platformId' ? platformName(a.platformId) : (a as unknown as Record<string, unknown>)[fieldSorted];
            const rawB = fieldSorted === 'platformId' ? platformName(b.platformId) : (b as unknown as Record<string, unknown>)[fieldSorted];
            const aVal = String(rawA ?? '');
            const bVal = String(rawB ?? '');
            if (aVal === bVal) return 0;
            const cmp = aVal < bVal ? -1 : 1;
            return orderSorted === 'ASC' ? cmp : -cmp;
        });

    const openEdit = (instrument: Instrument) => {
        editingInstrument.value = instrument;
        editForm.value = {
            product: instrument.product,
            issueDate: instrument.issueDate || '',
            dueDate: instrument.dueDate,
            rateType: instrument.rateType || 'PRE',
            remunerationIndex: instrument.remunerationIndex || '',
            tax: instrument.tax,
            quantity: String(instrument.quantity),
            pu: instrument.pu || '',
            puDegassed: instrument.puDegassed || '',
            financialValue: instrument.financialValue || '',
            diFuturo: instrument.diFuturo || '',
            additionalSpread: instrument.additionalSpread || '',
        };
        showEditModal.value = true;
    };

    const closeEdit = () => {
        showEditModal.value = false;
        editingInstrument.value = null;
        editForm.value = null;
    };

    const openDetails = (instrument: Instrument) => {
        viewingInstrument.value = instrument;
        showDetailsModal.value = true;
        puCheck.value = null;
        puCheckLoading.value = true;
        fundingApi.checkPuDesagiado(instrument.id).then((result) => {
            puCheckLoading.value = false;
            if (!result.ok) {
                toast.error(`Não foi possível conferir o PU Desagiado: ${result.message}`);
                return;
            }
            puCheck.value = result.data;
        });
    };

    const closeDetails = () => {
        showDetailsModal.value = false;
        viewingInstrument.value = null;
        puCheck.value = null;
    };

    const updateEditField = (field: keyof EditForm, value: string) => {
        if (!editForm.value) return;
        editForm.value = { ...editForm.value, [field]: value };
    };

    const handleSaveEdit = async () => {
        if (!editingInstrument.value || !editForm.value) return;

        isSending.value = true;
        const result = await fundingApi.updateInstrument(editingInstrument.value.id, {
            product: editForm.value.product,
            issue_date: editForm.value.issueDate,
            due_date: editForm.value.dueDate,
            rate_type: editForm.value.rateType,
            remuneration_index: editForm.value.rateType === 'POS' ? editForm.value.remunerationIndex : null,
            tax: parseDecimal(editForm.value.tax),
            quantity: parseInt(editForm.value.quantity, 10),
            pu: parseDecimal(editForm.value.pu),
            pu_degassed: editForm.value.puDegassed ? parseDecimal(editForm.value.puDegassed) : undefined,
            financial_value: parseDecimal(editForm.value.financialValue),
            di_futuro: editForm.value.diFuturo ? parseDecimal(editForm.value.diFuturo) : null,
            additional_spread: editForm.value.additionalSpread ? parseDecimal(editForm.value.additionalSpread) : null,
        });
        isSending.value = false;

        if (!result.ok) {
            toast.error(result.message);
            return;
        }

        toast.success('Emissão atualizada.');
        closeEdit();
        loadInstruments();
    };

    const handleCancel = async (instrument: Instrument) => {
        if (!window.confirm(`Deseja realmente cancelar a emissão de ${instrument.productFormatted} (vencimento ${extractAndConvertFrom(instrument.dueDate)})?`)) return;

        busyId.value = instrument.id;
        const result = await fundingApi.cancelInstrument(instrument.id);
        busyId.value = null;

        if (!result.ok) {
            toast.error(result.message);
            return;
        }

        toast.success('Emissão cancelada.');
        loadInstruments();
    };

    const handleRegisterCustody = async (instrument: Instrument) => {
        busyId.value = instrument.id;
        const result = await fundingApi.registerCustody(instrument.id);
        busyId.value = null;

        if (!result.ok) {
            toast.error(result.message);
            return;
        }

        toast.info(
            result.data.status === 'SUCCESS'
                ? 'Custódia registrada na B3.'
                : 'Registro de custódia enviado à B3 (integração ainda em stub - ver docs/api-contract.md).'
        );
        loadInstruments();
    };

    const handleRegisterCollateral = async (instrument: Instrument) => {
        busyId.value = instrument.id;
        const result = await fundingApi.registerCollateral(instrument.id);
        busyId.value = null;

        if (!result.ok) {
            toast.error(result.message);
            return;
        }

        toast.info(
            result.data.status === 'SUCCESS'
                ? 'Passivo registrado na Dimensa.'
                : 'Registro de passivo enviado à Dimensa (integração ainda em stub - ver docs/api-contract.md).'
        );
        loadInstruments();
    };

    const renderRegistrationCell = (instrument: Instrument, type: RegistrationType) => {
        const registration = findRegistration(instrument, type);
        if (!registration) return <span className="text-gray-400 dark:text-gray-500 text-sm">—</span>;
        return <BadgeStatus color={REGISTRATION_BADGE_COLOR[registration.status] || 'gray'} text={registration.statusFormatted} />;
    };

    const renderActions = (instrument: Instrument) => {
        const busy = busyId.value === instrument.id;
        const noActionsLeft = !isEditable(instrument.status)
            && !canRegisterCustody(instrument.status)
            && !canRegisterCollateral(instrument.status);

        return (
            <div className="flex items-center justify-center gap-2">
                <button
                    onClick={() => openDetails(instrument)}
                    className="text-gray-500 hover:text-teal-600 dark:text-gray-400 dark:hover:text-teal-400"
                    title="Ver detalhes"
                >
                    <FaEye />
                </button>
                {isEditable(instrument.status) && (
                    <button
                        onClick={() => openEdit(instrument)}
                        disabled={busy}
                        className="text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 disabled:opacity-50"
                        title="Editar"
                    >
                        <FaEdit />
                    </button>
                )}
                {isEditable(instrument.status) && (
                    <button
                        onClick={() => handleCancel(instrument)}
                        disabled={busy}
                        className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 disabled:opacity-50"
                        title="Cancelar emissão"
                    >
                        <FaBan />
                    </button>
                )}
                {canRegisterCustody(instrument.status) && (
                    <button
                        onClick={() => handleRegisterCustody(instrument)}
                        disabled={busy}
                        className="text-xs px-2 py-1 border rounded text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:border-slate-600 dark:hover:bg-slate-700 disabled:opacity-50"
                        title="Registrar custódia na B3"
                    >
                        B3
                    </button>
                )}
                {canRegisterCollateral(instrument.status) && (
                    <button
                        onClick={() => handleRegisterCollateral(instrument)}
                        disabled={busy}
                        className="text-xs px-2 py-1 border rounded text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:border-slate-600 dark:hover:bg-slate-700 disabled:opacity-50"
                        title="Registrar passivo na Dimensa"
                    >
                        Dimensa
                    </button>
                )}
                {noActionsLeft && <FaLock className="text-gray-400 dark:text-gray-500" title="Emissão finalizada" />}
            </div>
        );
    };

    const renderRows = () =>
        visibleInstruments.map((row, idx) => (
            <tr
                key={row.id}
                onClick={() => openDetails(row)}
                title="Clique para ver detalhes"
                className={`cursor-pointer ${idx % 2 === 0 ? 'bg-slate-50 dark:bg-slate-800' : 'dark:bg-slate-900'} hover:bg-slate-200 dark:hover:bg-slate-700 border-b border-gray-200 dark:border-gray-700`}
            >
                <td className="py-2 px-2 text-sm font-semibold">{row.productFormatted}</td>
                <td className="py-2 px-2 text-sm text-right font-mono">{row.commandNumber ?? '—'}</td>
                <td className="py-2 px-2 text-sm">{platformName(row.platformId)}</td>
                <td className="py-2 px-2 text-sm">{row.issueDate ? extractAndConvertFrom(row.issueDate) : '—'}</td>
                <td className="py-2 px-2 text-sm whitespace-nowrap">{formatCreatedAt(row.createdAt)}</td>
                <td className="py-2 px-2 text-sm">{extractAndConvertFrom(row.dueDate)}</td>
                <td className="py-2 px-2 text-sm">{formatIndexador(row)}</td>
                <td className="py-2 px-2 text-sm">{formatTax(row.tax)}</td>
                <td className="py-2 px-2 text-sm text-right">{row.quantity}</td>
                <td className="py-2 px-2 text-sm text-right">{row.puDegassed !== null ? currencyWithPrefix(parseDecimal(row.puDegassed)) : '—'}</td>
                <td className="py-2 px-2 text-center"><BadgeStatus color={STATUS_BADGE_COLOR[row.status] || 'gray'} text={row.statusFormatted} /></td>
                <td className="py-2 px-2 text-center">{renderRegistrationCell(row, 'CUSTODY')}</td>
                <td className="py-2 px-2 text-center">{renderRegistrationCell(row, 'COLLATERAL')}</td>
                <td className="py-2 px-2 text-center" onClick={(e) => e.stopPropagation()}>{renderActions(row)}</td>
            </tr>
        ));

    const editModalBody = editForm.value ? (
        <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Produto</label>
                    <select
                        value={editForm.value.product}
                        onChange={(e) => updateEditField('product', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    >
                        {PRODUCT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data da Aplicação</label>
                    <input
                        type="date"
                        value={editForm.value.issueDate}
                        onChange={(e) => updateEditField('issueDate', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vencimento</label>
                    <input
                        type="date"
                        value={editForm.value.dueDate}
                        onChange={(e) => updateEditField('dueDate', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Indexador</label>
                    <select
                        value={editForm.value.rateType}
                        onChange={(e) => updateEditField('rateType', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    >
                        {RATE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                {editForm.value.rateType === 'POS' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Índice (ex: CDI)</label>
                        <input
                            type="text"
                            value={editForm.value.remunerationIndex}
                            onChange={(e) => updateEditField('remunerationIndex', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                        />
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Taxa (%)</label>
                    <input
                        type="text"
                        value={editForm.value.tax}
                        onChange={(e) => updateEditField('tax', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quantidade</label>
                    <input
                        type="number"
                        value={editForm.value.quantity}
                        onChange={(e) => updateEditField('quantity', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PU (R$)</label>
                    <input
                        type="text"
                        value={editForm.value.pu}
                        onChange={(e) => updateEditField('pu', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    />
                </div>
                {editForm.value.product !== 'DB_DI' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PU Desagiado (R$)</label>
                        <input
                            type="text"
                            value={editForm.value.puDegassed}
                            onChange={(e) => updateEditField('puDegassed', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                        />
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor (R$)</label>
                    <input
                        type="text"
                        value={editForm.value.financialValue}
                        onChange={(e) => updateEditField('financialValue', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    />
                </div>
                {editForm.value.rateType === 'POS' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">DI Futuro (%) - opcional</label>
                        <input
                            type="text"
                            value={editForm.value.diFuturo}
                            onChange={(e) => updateEditField('diFuturo', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                        />
                    </div>
                )}
                {editForm.value.product === 'DB_DI' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Spread Adicional (%) - opcional</label>
                        <input
                            type="text"
                            value={editForm.value.additionalSpread}
                            onChange={(e) => updateEditField('additionalSpread', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                        />
                    </div>
                )}
            </div>
        </div>
    ) : null;

    const detailsModalBody = viewingInstrument.value ? (
        <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Produto</div>
                    <div className="text-sm">{viewingInstrument.value.productFormatted}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Nº Comando</div>
                    <div className="text-sm font-mono">{viewingInstrument.value.commandNumber ?? '—'}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Corretora</div>
                    <div className="text-sm">
                        {platformName(viewingInstrument.value.platformId)}
                        {(() => {
                            const cetip = cetipAccountFor(viewingInstrument.value.platformId, viewingInstrument.value.product);
                            return cetip ? <span className="text-gray-400 dark:text-gray-500"> — CETIP {cetip}</span> : null;
                        })()}
                    </div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</div>
                    <div className="text-sm"><BadgeStatus color={STATUS_BADGE_COLOR[viewingInstrument.value.status] || 'gray'} text={viewingInstrument.value.statusFormatted} /></div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Data da Aplicação</div>
                    <div className="text-sm">{viewingInstrument.value.issueDate ? extractAndConvertFrom(viewingInstrument.value.issueDate) : '—'}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Vencimento</div>
                    <div className="text-sm">{extractAndConvertFrom(viewingInstrument.value.dueDate)}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Indexador</div>
                    <div className="text-sm">{formatIndexador(viewingInstrument.value)}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Taxa</div>
                    <div className="text-sm">{formatTax(viewingInstrument.value.tax)}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Quantidade</div>
                    <div className="text-sm">{viewingInstrument.value.quantity}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">PU</div>
                    <div className="text-sm">{viewingInstrument.value.pu ? currencyWithPrefix(parseDecimal(viewingInstrument.value.pu)) : '—'}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">PU Desagiado</div>
                    <div className="text-sm">{viewingInstrument.value.puDegassed ? currencyWithPrefix(parseDecimal(viewingInstrument.value.puDegassed)) : '—'}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Valor</div>
                    <div className="text-sm">{viewingInstrument.value.financialValue ? currencyWithPrefix(parseDecimal(viewingInstrument.value.financialValue)) : '—'}</div>
                </div>
                {viewingInstrument.value.rateType === 'POS' && (
                    <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">DI Futuro</div>
                        <div className="text-sm">{viewingInstrument.value.diFuturo ? `${parseDecimal(viewingInstrument.value.diFuturo).toFixed(2)}%` : '—'}</div>
                    </div>
                )}
                {viewingInstrument.value.additionalSpread && (
                    <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Spread Adicional</div>
                        <div className="text-sm">{`${parseDecimal(viewingInstrument.value.additionalSpread).toFixed(2)}%`}</div>
                    </div>
                )}
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Criado por</div>
                    <div className="text-sm">{viewingInstrument.value.createdBy}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Cadastrado em</div>
                    <div className="text-sm">{formatCreatedAt(viewingInstrument.value.createdAt)}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Última atualização</div>
                    <div className="text-sm">{formatCreatedAt(viewingInstrument.value.updatedAt)}</div>
                </div>
                <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">ID do instrumento</div>
                    <div className="text-xs font-mono text-gray-400">{viewingInstrument.value.id}</div>
                </div>
            </div>

            {puCheckLoading.value && (
                <div className="text-sm text-gray-400">Conferindo PU Desagiado...</div>
            )}
            {!puCheckLoading.value && puCheck.value?.applicable && (
                <div className={`text-sm p-3 rounded border ${puCheck.value.diverges
                    ? 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                    : 'bg-green-50 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'}`}
                >
                    <div className="font-medium">
                        {puCheck.value.diverges ? 'Divergência na conferência do PU Desagiado' : 'PU Desagiado confere'}
                    </div>
                    <div className="text-xs mt-1">
                        Esperado: {currencyWithPrefix(parseDecimal(puCheck.value.expectedPuDegassed || '0'))} · Recebido: {currencyWithPrefix(parseDecimal(puCheck.value.reportedPuDegassed || '0'))}
                        {' · '}Diferença: {(parseDecimal(puCheck.value.relativeDifference || '0') * 100).toFixed(2)}%
                        {' · '}{puCheck.value.businessDays} dias úteis
                    </div>
                </div>
            )}
            {!puCheckLoading.value && puCheck.value && !puCheck.value.applicable && (
                <div className="text-xs text-gray-400">Conferência de PU Desagiado não aplicável: {puCheck.value.reason}</div>
            )}

            <div>
                <div className="text-sm font-semibold mb-2">Registros de custódia/passivo</div>
                {viewingInstrument.value.registrations.length === 0 ? (
                    <div className="text-sm text-gray-400">Nenhum registro ainda.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                                <th className="pb-1">Tipo</th>
                                <th className="pb-1">Status</th>
                                <th className="pb-1">Código externo</th>
                                <th className="pb-1">Registrado em</th>
                            </tr>
                        </thead>
                        <tbody>
                            {viewingInstrument.value.registrations.map((r) => (
                                <tr key={r.id} className="border-t border-gray-100 dark:border-slate-700">
                                    <td className="py-1">{r.typeFormatted}</td>
                                    <td className="py-1"><BadgeStatus color={REGISTRATION_BADGE_COLOR[r.status] || 'gray'} text={r.statusFormatted} /></td>
                                    <td className="py-1">{r.externalCode || '—'}</td>
                                    <td className="py-1">{r.registeredAt ? formatCreatedAt(r.registeredAt) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    ) : null;

    const detailsModalFooter = (
        <button
            onClick={closeDetails}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 dark:border-slate-600 dark:text-white dark:hover:bg-slate-700"
        >
            Fechar
        </button>
    );

    const editModalFooter = (
        <div className="flex gap-2">
            <button
                onClick={closeEdit}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 dark:border-slate-600 dark:text-white dark:hover:bg-slate-700"
            >
                Cancelar
            </button>
            <button
                onClick={handleSaveEdit}
                disabled={isSending.value}
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 dark:bg-teal-700 dark:hover:bg-teal-800"
            >
                Salvar Alterações
            </button>
        </div>
    );

    return (
        <div className="px-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            <div className="grid grid-cols-1">
                {isLoading && <Loading />}
                <PageHeader
                    title="Emissões (Funding)"
                    apiVersion=""
                    breadcrumb={[
                        { label: 'Tesouraria', url: '/funding/issuance/list' },
                        { label: 'Emissões', url: '#' },
                    ]}
                >
                    <div className="pt-4 flex justify-end gap-2">
                        <button
                            onClick={() => loadInstruments()}
                            className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 dark:border-slate-600 dark:text-white dark:hover:bg-slate-700"
                            title="Atualizar lista"
                        >
                            <IoIosRefresh /> Atualizar
                        </button>
                        <button
                            onClick={() => navigate('/funding/issuance/form')}
                            className="flex items-center gap-1 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm dark:bg-blue-600 dark:hover:bg-blue-700"
                        >
                            <FaPlus /> Nova Emissão
                        </button>
                    </div>
                </PageHeader>

                <div className="flex flex-wrap items-end gap-3 py-3 px-1 border-b border-gray-200 dark:border-gray-700">
                    <SelectFilter
                        id="filter-product"
                        label="Produto"
                        value={filterProduct}
                        onChange={setFilterProduct}
                        options={PRODUCT_OPTIONS}
                        defaultVoidOption
                        defaultLabel="Todos..."
                        className="w-32"
                    />
                    <SelectFilter
                        id="filter-status"
                        label="Status"
                        value={filterStatus}
                        onChange={setFilterStatus}
                        options={STATUS_OPTIONS}
                        defaultVoidOption
                        defaultLabel="Todos..."
                        className="w-52"
                    />
                    <SelectFilter
                        id="filter-platform"
                        label="Corretora"
                        value={filterPlatform}
                        onChange={setFilterPlatform}
                        options={platformOptions}
                        defaultVoidOption
                        defaultLabel="Todas..."
                        className="w-44"
                    />
                    <div>
                        <label htmlFor="filter-created-from" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Cadastrado de</label>
                        <input
                            id="filter-created-from"
                            type="date"
                            value={filterCreatedFrom}
                            onChange={(e) => setFilterCreatedFrom(e.target.value)}
                            className="p-2 border border-gray-300 rounded text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                        />
                    </div>
                    <div>
                        <label htmlFor="filter-created-to" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">até</label>
                        <input
                            id="filter-created-to"
                            type="date"
                            value={filterCreatedTo}
                            onChange={(e) => setFilterCreatedTo(e.target.value)}
                            className="p-2 border border-gray-300 rounded text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 pb-2">
                        <input
                            type="checkbox"
                            checked={includeCancelled}
                            onChange={(e) => { setIncludeCancelled(e.target.checked); setPage(1); }}
                            className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
                        />
                        Incluir cancelados
                    </label>
                    <button
                        onClick={clearFilters}
                        className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                    >
                        Limpar filtros
                    </button>
                </div>

                <ATable headers={TABLE_HEADERS} fieldSorted={fieldSorted} orderSorted={orderSorted} onSort={handleSort} autoResize={true}>
                    {visibleInstruments.length === 0 ? (
                        <tr>
                            <td colSpan={TABLE_HEADERS.length} className="py-8 text-center text-gray-500 dark:text-gray-400 text-lg">
                                Nenhuma emissão encontrada.
                            </td>
                        </tr>
                    ) : renderRows()}
                </ATable>

                <PageFooter
                    currentPage={page - 1}
                    totalPages={Math.max(1, Math.ceil(total / pageSize))}
                    itemsPerPage={pageSize}
                    totalElements={total}
                    onPageChange={(p) => setPage(p + 1)}
                    onItemsPerPageChange={(size) => { setPageSize(size); setPage(1); }}
                    itemsLabel="emissões"
                />
            </div>

            <Dialog
                id="edit-instrument-modal"
                show={showEditModal.value}
                onClose={closeEdit}
                header="Editar Emissão"
                size="lg"
                body={editModalBody}
                footer={editModalFooter}
            />

            <Dialog
                id="instrument-details-modal"
                show={showDetailsModal.value}
                onClose={closeDetails}
                header="Detalhes da Emissão"
                size="lg"
                body={detailsModalBody}
                footer={detailsModalFooter}
            />
        </div>
    );
};

export default IssuanceListPage;
